// Funnel tag for the Meta `Lead` event (#925).
//
// Every lead form fires the same pixel `Lead`, so Meta can't tell a wedding
// lead from a drop-off one and each ad set optimizes on any lead. Both legs of
// the Lead (the browser pixel and this function's CAPI event) now carry a
// `content_category` naming the funnel, so Meta can have one custom conversion
// per funnel.
//
// One import, itself import-free (social-enrich/services.ts, the single list
// of budget services, #1290): the SPA imports this file too
// (lib/lead-tracking.ts, hooks/useSubmitEvent.ts, pages/OrderPage.tsx), so the
// browser leg and the server leg normalize the value the same way and the
// deduped pair agrees.
import type { BudgetService } from "../social-enrich/services.ts";

/** The budget services (#1289, #533 scope v2): one ad set and custom conversion each. */
export { BUDGET_SERVICES } from "../social-enrich/services.ts";
export type { BudgetService };

const MAX_CATEGORY_LENGTH = 80;

/**
 * The category for a plan wizard lead, from its `events.event_type`. The
 * wizard's `venue` service is the `event_space` budget service. Anything else
 * gets no category rather than a wrong one.
 */
export function leadCategoryForPlanService(service: string | null | undefined): string | undefined {
  switch (service) {
    case "wedding":
    case "corporate":
    case "drop_off":
    case "live_service":
      return service;
    case "venue":
      return "event_space";
    default:
      return undefined;
  }
}

/** The category for an /order checkout. Mirrors `orderRedirectTarget` (lib/ordering.ts). */
export function orderLeadCategory(service: string | null | undefined): BudgetService {
  return service === "corporate" ? "corporate" : "drop_off";
}

/**
 * Turn a caller-supplied category into the value Meta receives: lowercase, with
 * any run of characters outside `a-z 0-9 _ -` turned into `-`, at most 80
 * characters, and no leading or trailing `-`. So an admin-typed slug like
 * `TG2026` still tags its funnel (`tg2026`). Cutting before trimming makes the
 * result a fixed point, which is what lets the browser and the server both run
 * it and still agree. Returns undefined for a non-string or an empty result.
 */
export function normalizeContentCategory(raw: unknown): string | undefined {
  if (typeof raw !== "string") return undefined;
  const value = raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .slice(0, MAX_CATEGORY_LENGTH)
    .replace(/^-+|-+$/g, "");
  return value || undefined;
}

/**
 * The CAPI `custom_data` for a Lead: the value-based-bidding pair (#278) when a
 * positive value is supplied, clamped to `maxValue` because this endpoint is
 * anon, plus the funnel's `content_category` (#925). Null when there is
 * neither, so a caller that sends neither gets the same payload as before.
 */
export function buildLeadCustomData(
  payload: { value?: unknown; currency?: unknown; content_category?: unknown },
  maxValue: number,
): Record<string, string | number> | null {
  const data: Record<string, string | number> = {};
  const raw = payload.value;
  if (typeof raw === "number" && Number.isFinite(raw) && raw > 0) {
    data.value = Math.min(raw, maxValue);
    data.currency = typeof payload.currency === "string" && payload.currency ? payload.currency : "CAD";
  }
  const category = normalizeContentCategory(payload.content_category);
  if (category) data.content_category = category;
  return Object.keys(data).length > 0 ? data : null;
}
